/**
 * Owns: PDP store UI (how-to-get-it rows, ATC presentation on form/sticky).
 * Exposes: getAddToCartGateState, syncPresentation, STORE_ATC_PRESENTATION_CHANGED_EVENT.
 * Consumes: store-inventory (API/rules), store-pricing (cart/store), product-form via getProductFormComponent.
 */

import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import {
  STORE_LOCATOR_SOURCE,
  buildDeliveryZipPatch,
  fetchCartAttributes,
  findStoreFromCartAttributes,
  getDeliveryZipFromCartAttributes,
  hasStoreCartAttributes,
  loadStoreCatalog,
  resolveStoreForLocationDetail,
} from '@theme/store-pricing';
import { patchCartAttributes } from '@theme/store-locator-cart';
import { resolveDeliveryZipCheck } from '@theme/store-delivery-zip';
import {
  DELIVERY_ZIP_CHECKED_EVENT,
  STORE_ATC_PRESENTATION_CHANGED_EVENT,
  STORE_AVAILABILITY_LOADING_EVENT,
  STORE_INVENTORY_SETTLED_EVENT,
  STORE_SELECTED_EVENT,
} from '@theme/store-events';
import {
  canAddToCartForDelivery,
  getPickUpEnabledForLocation,
  getProductFormComponent,
  getStoreInventoryConfig,
  allowsContinueSellingAtStore,
  parseStoreLocatorAvailabilities,
  readContinueSellingSignalsFromElement,
  resolveStoreInventoryForLocation,
} from '@theme/store-inventory';
import { DeliveryZipModal } from '@theme/delivery-zip-modal';
import { StoreLocationDetail } from '@theme/store-location-detail';

export {
  DELIVERY_ZIP_CHECKED_EVENT,
  STORE_ATC_PRESENTATION_CHANGED_EVENT,
};

/**
 * @typedef {Object} StoreAddToCartGateState
 * @property {boolean} canAdd
 */

/**
 * @typedef {Object} StorePresentationSyncOptions
 * @property {boolean} variantAvailable
 * @property {boolean} [canAdd] - When omitted, derived from gate + quantity rules passed by caller.
 */

/**
 * @typedef {HTMLElement & {
 *   getAddToCartGateState: () => StoreAddToCartGateState;
 *   getFulfillmentQuantityLimit: () => number | null;
 *   canAddToCartForFulfillment: () => boolean;
 *   canAddToCartAtStore: () => boolean;
 *   syncPresentation: (options: StorePresentationSyncOptions) => void;
 *   syncAddToCartPresentation: (canAdd: boolean) => void;
 *   refreshInventory: () => Promise<void>;
 *   getInventoryStatus: () => import('@theme/store-inventory').StoreInventoryStatus;
 *   clearDeliveryZip: () => Promise<void>;
 * }} StoreProductAvailabilityElement
 */

/**
 * @param {HTMLElement | null | undefined} element
 * @returns {StoreProductAvailabilityElement | null}
 */
export function asStoreProductAvailability(element) {
  if (!(element instanceof HTMLElement)) return null;
  if (typeof element.getAddToCartGateState !== 'function') {
    return null;
  }
  return /** @type {StoreProductAvailabilityElement} */ (element);
}

/**
 * @typedef {import('@theme/store-inventory').ResolvedStoreInventory & {
 *   pickUpEnabled: boolean;
 *   pickupAvailable: boolean;
 * }} PdpAvailabilityState
 */

/**
 * @typedef {Object} StoreProductAvailabilityRefs
 * @property {HTMLElement} [cta]
 * @property {HTMLElement} [howToGetIt]
 * @property {HTMLElement} [storeLocationLine]
 * @property {HTMLElement} [deliveryStatus]
 * @property {HTMLElement} [deliveryStatusDot]
 * @property {HTMLElement} [deliveryStatusText]
 * @property {HTMLElement} [storeStatus]
 * @property {HTMLElement} [storeStatusDot]
 * @property {HTMLElement} [storeStatusText]
 * @property {HTMLElement} [deliveryZipLine]
 */

/**
 * @extends {Component<StoreProductAvailabilityRefs>}
 */
class StoreProductAvailability extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #fetchAbort;

  /** @type {number} */
  #inventoryRequestId = 0;

  /** @type {PdpAvailabilityState | null} */
  #state = null;

  /** @type {string} */
  #storeDisplayLabel = '';

  /** @type {Date | null} */
  #lastInventoryCheckedAt = null;

  /**
   * Runtime mirror of cart.attributes.delivery_zip (see #applyDeliveryZipCheck).
   * @type {{ zip: string; status: import('@theme/store-inventory').StoreInventoryStatus; quantity: number | null; inServiceArea: boolean } | null}
   */
  #deliveryZipCheck = null;

  /** @type {Promise<void> | null} */
  #refreshInventoryPromise = null;

  /** @type {string | null} */
  #refreshInventoryVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (this.dataset.storeInventoryEnabled !== 'true') return;

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleDocumentVariantUpdate, { signal });
    document.addEventListener(STORE_SELECTED_EVENT, this.#handleStoreChange, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(DELIVERY_ZIP_CHECKED_EVENT, this.#handleDeliveryZipChecked, { signal });

    void this.#refreshInventory();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#fetchAbort?.abort();
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * @returns {boolean}
   */
  canAddToCartAtStore() {
    return this.canAddToCartForFulfillment();
  }

  /**
   * @returns {boolean}
   */
  canAddToCartForFulfillment() {
    return this.getAddToCartGateState().canAdd;
  }

  /**
   * @returns {StoreAddToCartGateState}
   */
  getAddToCartGateState() {
    if (this.dataset.storeInventoryEnabled !== 'true') {
      return { canAdd: true };
    }

    const state = this.#state;
    if (!state?.hasStoreSelected) {
      return { canAdd: false };
    }

    if (state.status === 'loading' || state.status === 'no_store') {
      return { canAdd: false };
    }

    return { canAdd: canAddToCartForDelivery(state.status) };
  }

  /**
   * Numeric cap for store inventory at the current location, or null when unknown / not applicable.
   * Absolute total allowed for this variant at the location (same semantics as cart validation).
   * @returns {number | null}
   */
  getFulfillmentQuantityLimit() {
    if (this.dataset.storeInventoryEnabled !== 'true') return null;

    const state = this.#state;
    if (!state?.hasStoreSelected) return null;
    if (!canAddToCartForDelivery(state.status)) return null;

    return state.quantity;
  }

  /**
   * @returns {import('@theme/store-inventory').StoreInventoryStatus}
   */
  getInventoryStatus() {
    return this.#state?.status ?? 'no_store';
  }

  /**
   * Refreshes inventory for the current cart store (dedupes concurrent callers for the same variant).
   * @returns {Promise<void>}
   */
  refreshInventory() {
    if (this.dataset.storeInventoryEnabled !== 'true') {
      return Promise.resolve();
    }

    const variantId = this.dataset.currentVariantId?.trim() ?? '';

    if (this.#refreshInventoryPromise && this.#refreshInventoryVariantId === variantId) {
      return this.#refreshInventoryPromise;
    }

    this.#resetRefreshInventoryCache();
    this.#refreshInventoryVariantId = variantId;
    const refreshVariantId = variantId;

    this.#refreshInventoryPromise = this.#refreshInventory().finally(() => {
      if (this.#refreshInventoryVariantId === refreshVariantId) {
        this.#resetRefreshInventoryCache();
      }
    });

    return this.#refreshInventoryPromise;
  }

  #resetRefreshInventoryCache() {
    this.#fetchAbort?.abort();
    this.#refreshInventoryPromise = null;
    this.#refreshInventoryVariantId = null;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleDocumentVariantUpdate = (event) => {
    if (!this.isConnected || this.dataset.storeInventoryEnabled !== 'true') return;

    const eventProductId = String(event.detail.data.productId ?? '');
    const myProductId = String(this.dataset.productId ?? '');
    const variantPicker =
      event.target instanceof Element ? event.target.closest('variant-picker') : null;
    const fromMyPicker =
      event.target instanceof Element &&
      this.closest('.product-information, dialog')?.contains(event.target) &&
      variantPicker instanceof HTMLElement &&
      String(variantPicker.dataset.productId ?? '') === myProductId;

    if (eventProductId && eventProductId !== myProductId && !fromMyPicker) return;

    const variantId = event.detail.resource?.id;
    if (variantId) {
      this.dataset.currentVariantId = String(variantId);
    }

    this.#syncAvailabilitiesFromVariantHtml(event.detail.data.html);

    void this.refreshInventory();
  };

  #handleStoreChange = () => {
    this.#resetRefreshInventoryCache();
    void this.refreshInventory();
  };

  /** @param {CustomEvent} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source !== STORE_LOCATOR_SOURCE) return;
    this.#resetRefreshInventoryCache();
    void this.refreshInventory();
  };

  /**
   * @returns {number}
   */
  #nextInventoryRequestId() {
    this.#inventoryRequestId += 1;
    return this.#inventoryRequestId;
  }

  /**
   * @param {number} requestId
   * @param {string} [expectedVariantId]
   * @returns {boolean}
   */
  #isStaleRequest(requestId, expectedVariantId) {
    if (this.dataset.inventoryRequestId !== String(requestId)) return true;
    if (expectedVariantId && this.dataset.currentVariantId !== expectedVariantId) return true;
    return false;
  }

  /**
   * When a refresh is superseded (variant/store changed mid-flight), queue another pass
   * so the UI does not keep stale inventory state.
   *
   * @param {number} requestId
   * @param {string} [expectedVariantId]
   */
  #scheduleRefreshIfSuperseded(requestId, expectedVariantId) {
    if (!this.#isStaleRequest(requestId, expectedVariantId)) return;

    queueMicrotask(() => {
      if (this.isConnected) void this.refreshInventory();
    });
  }

  /**
   * @param {import('@theme/store-pricing').StoreCatalogEntry | null | undefined} store
   * @returns {string}
   */
  #formatStoreLabel(store) {
    const entry = /** @type {{ displayName?: string; address?: { city?: string; state?: string } } | null | undefined} */ (
      store
    );
    const city = entry?.address?.city?.trim();
    const state = entry?.address?.state?.trim();
    if (city && state) return `${city}, ${state}`;
    return entry?.displayName?.trim() ?? '';
  }

  /**
   * @returns {ReturnType<typeof parseStoreLocatorAvailabilities>}
   */
  #getLocatorAvailabilities() {
    return parseStoreLocatorAvailabilities(this.dataset.storeLocatorAvailabilities);
  }

  /**
   * @param {Document | Element | undefined | null} html
   */
  #syncAvailabilitiesFromVariantHtml(html) {
    const productId = this.dataset.productId?.trim();
    if (!productId || !html) return;

    const selector = `store-product-availability[data-product-id="${CSS.escape(productId)}"]`;
    const fresh = html.querySelector(selector);
    if (!(fresh instanceof HTMLElement)) return;

    const availabilities = fresh.dataset.storeLocatorAvailabilities;
    if (availabilities != null) {
      this.dataset.storeLocatorAvailabilities = availabilities;
    }

    const inventoryPolicy = fresh.dataset.inventoryPolicy;
    if (inventoryPolicy != null) {
      this.dataset.inventoryPolicy = inventoryPolicy;
    }

    const inStockInStore = fresh.dataset.inStockInStore;
    if (inStockInStore != null) {
      this.dataset.inStockInStore = inStockInStore;
    }
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   * @param {boolean} showCount
   * @param {boolean} pickUpEnabled
   * @param {boolean} pickupAvailable
   * @param {number | null} quantity
   * @returns {PdpAvailabilityState}
   */
  #buildPdpState(status, showCount, pickUpEnabled, pickupAvailable, quantity) {
    return {
      status,
      canAddToCart: canAddToCartForDelivery(status),
      hasStoreSelected: true,
      quantity,
      showCount,
      pickUpEnabled,
      pickupAvailable,
    };
  }

  /**
   * @param {boolean} isLoading
   */
  #dispatchStoreAvailabilityLoading(isLoading) {
    if (this.dataset.storeInventoryEnabled !== 'true') return;

    this.dispatchEvent(
      new CustomEvent(STORE_AVAILABILITY_LOADING_EVENT, {
        bubbles: true,
        detail: { loading: isLoading, productId: this.dataset.productId ?? '' },
      })
    );
  }

  #requestProductFormSync() {
    if (this.dataset.storeInventoryEnabled !== 'true') return;

    const productId = this.dataset.productId?.trim();
    if (!productId) return;

    this.dispatchEvent(
      new CustomEvent(STORE_INVENTORY_SETTLED_EVENT, {
        bubbles: true,
        detail: { productId },
      })
    );
  }

  /** @param {CustomEvent} event */
  #handleDeliveryZipChecked = (event) => {
    if (this.dataset.storeInventoryEnabled !== 'true' || !this.#state?.hasStoreSelected) return;

    const detail = event.detail;
    if (!detail || typeof detail !== 'object' || !('zip' in detail)) return;

    const zip = String(detail.zip ?? '').trim();
    if (!zip) return;

    const inServiceArea = Boolean(detail.inServiceArea);

    if (!inServiceArea) {
      this.#applyDeliveryZipCheck({
        zip,
        status: 'out_of_stock',
        quantity: null,
        inServiceArea: false,
      });
      return;
    }

    const status = this.#state?.status ?? 'unavailable';
    const quantity = this.#state?.quantity ?? null;

    this.#applyDeliveryZipCheck({
      zip,
      status,
      quantity,
      inServiceArea: true,
    });
  };

  /**
   * @param {{ zip: string; status: import('@theme/store-inventory').StoreInventoryStatus; quantity: number | null; inServiceArea: boolean }} check
   */
  #applyDeliveryZipCheck(check) {
    this.#deliveryZipCheck = check;

    if (!this.#state?.hasStoreSelected) return;

    this.#renderState(this.#state);
  }

  /**
   * @param {import('@theme/store-pricing').StoreCatalogEntry | null | undefined} store
   * @param {Record<string, string | number | boolean | undefined>} cartAttributes
   */
  #hydrateDeliveryZipFromCart(store, cartAttributes) {
    if (!this.#state?.hasStoreSelected) {
      this.#deliveryZipCheck = null;
      return;
    }

    const zip = getDeliveryZipFromCartAttributes(cartAttributes);
    if (!zip) {
      this.#deliveryZipCheck = null;
      return;
    }

    const check = resolveDeliveryZipCheck(store, zip, {
      status: this.#state.status,
      quantity: this.#state.quantity,
    });

    if (!check) {
      this.#deliveryZipCheck = null;
      return;
    }

    this.#applyDeliveryZipCheck(check);
  }

  async #refreshInventory() {
    this.#dispatchStoreAvailabilityLoading(true);

    const requestId = this.#nextInventoryRequestId();
    this.dataset.inventoryRequestId = String(requestId);
    const expectedVariantId = this.dataset.currentVariantId?.trim();

    try {
    const { baseUrl, authToken, threshold } = getStoreInventoryConfig(this);
    const variantId = expectedVariantId;
    const showCount = this.dataset.showInventoryQuantity !== 'false';

    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    const { signal } = this.#fetchAbort;

    const [cartAttributes, catalog] = await Promise.all([
      fetchCartAttributes(),
      Promise.resolve(loadStoreCatalog()),
    ]);

    if (this.#isStaleRequest(requestId, variantId)) {
      this.#scheduleRefreshIfSuperseded(requestId, variantId);
      return;
    }

    if (!hasStoreCartAttributes(cartAttributes)) {
      this.#storeDisplayLabel = '';
      this.#deliveryZipCheck = null;
      this.#state = {
        status: 'no_store',
        canAddToCart: false,
        hasStoreSelected: false,
        quantity: null,
        showCount,
        pickUpEnabled: false,
        pickupAvailable: false,
      };
      this.#renderState(this.#state);
      this.#requestProductFormSync();
      return;
    }

    const store = findStoreFromCartAttributes(catalog, cartAttributes);
    this.#storeDisplayLabel = this.#formatStoreLabel(store);

    const shopifyLocationId = store?.shopifyLocationId?.trim();
    const { availabilitiesByLocationId, pickUpByLocationId } = this.#getLocatorAvailabilities();
    const pickUpEnabled = getPickUpEnabledForLocation(shopifyLocationId, pickUpByLocationId);
    const continueSignals = readContinueSellingSignalsFromElement(this);
    const allowContinueSelling = allowsContinueSellingAtStore({
      inventoryPolicy: continueSignals.inventoryPolicy,
      inStockInStoreValues: continueSignals.inStockInStoreValues,
      storeCode: String(cartAttributes?.store_code ?? '').trim(),
    });

    if (!variantId) {
      this.#state = this.#buildPdpState('unavailable', showCount, pickUpEnabled, false, null);
      this.#hydrateDeliveryZipFromCart(store, cartAttributes);
      this.#renderState(this.#state);
      this.#requestProductFormSync();
      return;
    }

    if (!shopifyLocationId) {
      const status = allowContinueSelling ? 'in_stock' : 'unavailable';
      this.#state = this.#buildPdpState(status, showCount, pickUpEnabled, false, null);
      this.#hydrateDeliveryZipFromCart(store, cartAttributes);
      this.#renderState(this.#state);
      this.#requestProductFormSync();
      return;
    }

    this.#state = this.#buildPdpState('loading', showCount, pickUpEnabled, false, null);
    this.#renderState(this.#state);

    const availability = await resolveStoreInventoryForLocation({
      baseUrl,
      authToken,
      variantId,
      locationId: shopifyLocationId,
      availabilitiesByLocationId,
      threshold,
      pickUpEnabled,
      allowContinueSelling,
      signal,
    });

    if (this.#isStaleRequest(requestId, variantId)) {
      this.#scheduleRefreshIfSuperseded(requestId, variantId);
      return;
    }

    const effectiveShowCount = showCount && !availability.fromShopify && availability.quantity != null;

    this.#state = this.#buildPdpState(
      availability.deliveryStatus,
      effectiveShowCount,
      availability.pickUpEnabled,
      availability.pickupAvailable,
      availability.quantity
    );

    this.#hydrateDeliveryZipFromCart(store, cartAttributes);
    this.#renderState(this.#state);
    this.#requestProductFormSync();
    this.#lastInventoryCheckedAt = new Date();
    } finally {
      if (this.dataset.inventoryRequestId === String(requestId)) {
        this.#dispatchStoreAvailabilityLoading(false);
      }
    }
  }

  /** @param {Event} [event] */
  openDeliveryZipModal = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.dataset.storeInventoryEnabled !== 'true') return;

    const state = this.#state;
    if (!state?.hasStoreSelected || state.status === 'loading' || state.status === 'no_store') {
      return;
    }

    const modal = document.querySelector('delivery-zip-modal');
    if (!(modal instanceof DeliveryZipModal)) return;

    const productId = this.dataset.productId?.trim();
    if (productId) {
      modal.dataset.productId = productId;
    }

    modal.open(this.#deliveryZipCheck?.zip ?? '');
  };

  /** @param {Event} [event] */
  clearDeliveryZip = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.dataset.storeInventoryEnabled !== 'true') return;

    await patchCartAttributes(buildDeliveryZipPatch(''));

    this.#deliveryZipCheck = null;

    if (this.#state) {
      this.#renderState(this.#state);
    }
  };

  /** @param {Event} [event] */
  openStoreDetailModal = async (event) => {
    event?.preventDefault();

    if (this.dataset.storeInventoryEnabled !== 'true') return;

    const state = this.#state;
    if (!state?.hasStoreSelected || state.status === 'loading' || state.status === 'no_store') {
      return;
    }

    const catalog = loadStoreCatalog();
    const cartAttributes = await fetchCartAttributes();
    const store = resolveStoreForLocationDetail(catalog, cartAttributes, {
      fallbackDisplayName: this.#storeDisplayLabel,
    });

    const storeRow = this.#getStoreRowPresentation(state.status, state.pickUpEnabled, {
      showCount: state.showCount,
      quantity: state.quantity,
    });

    const detail = document.querySelector('store-location-detail');
    if (!(detail instanceof StoreLocationDetail)) {
      return;
    }

    detail.open({
      store: /** @type {import('@theme/store-location-detail').StoreLocationDetailStore | null} */ (store),
      displayStatus: storeRow.displayStatus,
      statusLabel: storeRow.text,
      lastCheckedAt: this.#lastInventoryCheckedAt,
    });
  };

  /**
   * @param {string} template
   * @param {number} count
   * @returns {string}
   */
  #interpolateCount(template, count) {
    return template.replace(/\{\{\s*count\s*\}\}/g, String(count));
  }

  /**
   * @param {string} template
   * @param {string} zip
   * @returns {string}
   */
  #interpolateZip(template, zip) {
    return template.replace(/\{\{\s*zip\s*\}\}/gi, zip);
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   * @param {{ showCount?: boolean; quantity?: number | null }} [options]
   * @param {{ zip?: string; inServiceArea?: boolean }} [deliveryContext]
   * @returns {string}
   */
  #getDeliveryCopy(status, options = {}, deliveryContext = {}) {
    const { zip = '', inServiceArea } = deliveryContext;
    const translations = Theme.translations ?? {};

    if (zip && inServiceArea === false) {
      const template =
        translations.how_to_get_it_delivery_unavailable_for_zip ?? 'Unavailable for {{ zip }}';
      return this.#interpolateZip(template, zip);
    }

    if (zip && inServiceArea === true) {
      return translations.how_to_get_it_delivery_available ?? 'Available';
    }

    return this.#getHowToGetItCopy(status, options).delivery;
  }

  /**
   * @returns {'how_to_get_it' | 'stock_status'}
   */
  #getDisplayLayout() {
    const layout = this.dataset.displayLayout?.trim();
    return layout === 'stock_status' ? 'stock_status' : 'how_to_get_it';
  }

  /**
   * @returns {'generic' | 'store_prefixed'}
   */
  #getStockCopyStyle() {
    return this.dataset.stockCopyStyle === 'store_prefixed' ? 'store_prefixed' : 'generic';
  }

  /**
   * @returns {boolean}
   */
  #shouldShowStoreLocation() {
    return this.dataset.showStoreLocation !== 'false';
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   * @param {{ showCount?: boolean; quantity?: number | null }} [options]
   * @returns {string}
   */
  #getStockStatusCopy(status, options = {}) {
    const { showCount = false, quantity = null } = options;
    const translations = Theme.translations ?? {};
    const loading = translations.store_inventory_loading ?? 'Checking availability…';
    const unavailable = translations.how_to_get_it_status_unavailable ?? 'Unavailable';
    const useStorePrefix = this.#getStockCopyStyle() === 'store_prefixed';

    switch (status) {
      case 'loading':
        return loading;
      case 'in_stock':
        return useStorePrefix
          ? (translations.how_to_get_it_store_in_stock ?? 'Store - In stock')
          : (translations.inventory_in_stock ?? 'In stock');
      case 'low': {
        const base = useStorePrefix
          ? (translations.how_to_get_it_store_low_stock ?? 'Store - Low stock')
          : (translations.inventory_low_stock ?? 'Low stock');
        if (showCount && quantity != null) {
          const countTemplate = useStorePrefix
            ? (translations.how_to_get_it_store_low_stock_count ?? 'Store - Low stock ({{ count }} left)')
            : (translations.inventory_low_stock_show_count_one ??
              translations.inventory_low_stock_show_count_other ??
              '{{ count }} left');
          if (useStorePrefix) {
            return this.#interpolateCount(countTemplate, quantity);
          }
          return `${base} (${this.#interpolateCount(countTemplate, quantity)})`;
        }
        return base;
      }
      case 'out_of_stock':
        return useStorePrefix
          ? (translations.how_to_get_it_store_out_of_stock ?? 'Store - Out of stock')
          : (translations.inventory_out_of_stock ?? 'Out of stock');
      case 'unavailable':
        return unavailable;
      default:
        return unavailable;
    }
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   * @param {{ showCount?: boolean; quantity?: number | null }} [options]
   * @returns {{ delivery: string; store: string }}
   */
  #getHowToGetItCopy(status, options = {}) {
    const { showCount = false, quantity = null } = options;
    const translations = Theme.translations ?? {};
    const loading = translations.store_inventory_loading ?? 'Checking availability…';
    const unavailable = translations.how_to_get_it_status_unavailable ?? 'Unavailable';

    switch (status) {
      case 'loading':
        return { delivery: loading, store: loading };
      case 'in_stock':
        return {
          delivery: translations.how_to_get_it_delivery_available ?? 'Available',
          store: translations.how_to_get_it_store_in_stock ?? 'Store - In stock',
        };
      case 'low': {
        const deliveryBase = translations.how_to_get_it_delivery_low_stock ?? 'Low stock';
        const storeBase = translations.how_to_get_it_store_low_stock ?? 'Store - Low stock';
        if (showCount && quantity != null) {
          const deliveryTemplate =
            translations.how_to_get_it_delivery_low_stock_count ?? 'Low stock ({{ count }} left)';
          const storeTemplate =
            translations.how_to_get_it_store_low_stock_count ?? 'Store - Low stock ({{ count }} left)';
          return {
            delivery: this.#interpolateCount(deliveryTemplate, quantity),
            store: this.#interpolateCount(storeTemplate, quantity),
          };
        }
        return { delivery: deliveryBase, store: storeBase };
      }
      case 'out_of_stock':
        return {
          delivery: translations.how_to_get_it_delivery_out_of_stock ?? 'Not available',
          store: translations.how_to_get_it_store_out_of_stock ?? 'Store - Out of stock',
        };
      case 'unavailable':
        return { delivery: unavailable, store: unavailable };
      default:
        return { delivery: unavailable, store: unavailable };
    }
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   * @param {boolean} pickUpEnabled
   * @param {{ showCount?: boolean; quantity?: number | null }} [options]
   * @returns {{ text: string; displayStatus: import('@theme/store-inventory').StoreInventoryStatus }}
   */
  #getStoreRowPresentation(status, pickUpEnabled, options = {}) {
    const text =
      this.#getDisplayLayout() === 'stock_status'
        ? this.#getStockStatusCopy(status, options)
        : this.#getHowToGetItCopy(status, options).store;

    return {
      text,
      displayStatus: status,
    };
  }

  /**
   * @param {HTMLElement | undefined} dotEl
   * @param {HTMLElement | undefined} statusEl
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   */
  #applyStatusDot(dotEl, statusEl, status) {
    const dotModifiers = [
      'how-to-get-it__status-dot--in_stock',
      'how-to-get-it__status-dot--low',
      'how-to-get-it__status-dot--out_of_stock',
      'how-to-get-it__status-dot--unavailable',
      'how-to-get-it__status-dot--loading',
    ];
    const statusModifiers = [
      'how-to-get-it__status--in_stock',
      'how-to-get-it__status--low',
      'how-to-get-it__status--out_of_stock',
      'how-to-get-it__status--unavailable',
      'how-to-get-it__status--loading',
    ];

    if (dotEl instanceof HTMLElement) {
      dotEl.classList.remove(...dotModifiers);
      if (status === 'in_stock') dotEl.classList.add('how-to-get-it__status-dot--in_stock');
      else if (status === 'low') dotEl.classList.add('how-to-get-it__status-dot--low');
      else if (status === 'out_of_stock') dotEl.classList.add('how-to-get-it__status-dot--out_of_stock');
      else if (status === 'unavailable') dotEl.classList.add('how-to-get-it__status-dot--unavailable');
      else dotEl.classList.add('how-to-get-it__status-dot--loading');
    }

    if (statusEl instanceof HTMLElement) {
      statusEl.classList.remove(...statusModifiers);
      if (status === 'in_stock') statusEl.classList.add('how-to-get-it__status--in_stock');
      else if (status === 'low') statusEl.classList.add('how-to-get-it__status--low');
      else if (status === 'out_of_stock') statusEl.classList.add('how-to-get-it__status--out_of_stock');
      else if (status === 'unavailable') statusEl.classList.add('how-to-get-it__status--unavailable');
      else statusEl.classList.add('how-to-get-it__status--loading');
    }
  }

  /**
   * @param {boolean} hasStoreSelected
   */
  #syncBuyButtonsVisibility(hasStoreSelected) {
    const productId = this.dataset.productId?.trim();
    if (!productId || this.dataset.storeInventoryEnabled !== 'true') return;

    const buyButtonsBlock = document
      .querySelector(`product-form-component[data-product-id="${productId}"]`)
      ?.closest('.buy-buttons-block');

    if (buyButtonsBlock instanceof HTMLElement) {
      buyButtonsBlock.hidden = !hasStoreSelected;
    }
  }

  /**
   * @param {PdpAvailabilityState} state
   */
  #renderState(state) {
    if (this.#getDisplayLayout() === 'stock_status') {
      this.#renderStockStatusState(state);
      return;
    }

    this.#renderHowToGetItState(state);
  }

  /**
   * @param {PdpAvailabilityState} state
   */
  #renderStockStatusState(state) {
    const {
      cta,
      howToGetIt,
      storeStatus,
      storeStatusDot,
      storeStatusText,
    } = this.refs;

    this.#syncBuyButtonsVisibility(state.hasStoreSelected);

    if (cta instanceof HTMLElement) {
      cta.hidden = state.hasStoreSelected;
    }

    if (howToGetIt instanceof HTMLElement) {
      howToGetIt.hidden = !state.hasStoreSelected;
    }

    if (!state.hasStoreSelected) return;

    const inventoryCopyOptions = { showCount: state.showCount, quantity: state.quantity };
    const storeRow = this.#getStoreRowPresentation(
      state.status,
      state.pickUpEnabled,
      inventoryCopyOptions
    );
    const storeDisplayStatus = state.status === 'no_store' ? 'loading' : storeRow.displayStatus;

    if (storeStatusText instanceof HTMLElement) {
      storeStatusText.textContent = storeRow.text;
    }

    this.#applyStatusDot(storeStatusDot, storeStatus, storeDisplayStatus);
  }

  /**
   * @param {PdpAvailabilityState} state
   */
  #renderHowToGetItState(state) {
    const {
      cta,
      howToGetIt,
      storeLocationLine,
      deliveryStatus,
      deliveryStatusDot,
      deliveryStatusText,
      deliveryZipLine,
      storeStatus,
      storeStatusDot,
      storeStatusText,
    } = this.refs;

    this.#syncBuyButtonsVisibility(state.hasStoreSelected);

    if (cta instanceof HTMLElement) {
      cta.hidden = state.hasStoreSelected;
    }

    if (howToGetIt instanceof HTMLElement) {
      howToGetIt.hidden = !state.hasStoreSelected;
    }

    if (!state.hasStoreSelected) return;

    if (storeLocationLine instanceof HTMLElement && this.#shouldShowStoreLocation()) {
      const location = this.#storeDisplayLabel.trim();
      if (location) {
        storeLocationLine.textContent = location;
        storeLocationLine.hidden = false;
      } else {
        storeLocationLine.textContent = '';
        storeLocationLine.hidden = true;
      }
    }

    const deliveryRowStatus =
      state.status === 'loading'
        ? 'loading'
        : this.#deliveryZipCheck?.status
          ?? (state.status === 'no_store' ? state.status : 'unavailable');
    const hasDeliveryZip = Boolean(this.#deliveryZipCheck?.zip);
    const deliveryDisplayStatus = !hasDeliveryZip
      ? 'loading'
      : this.#deliveryZipCheck?.inServiceArea === false
        ? 'out_of_stock'
        : this.#deliveryZipCheck?.inServiceArea === true
          ? 'in_stock'
          : 'unavailable';
    const inventoryCopyOptions = { showCount: state.showCount, quantity: state.quantity };
    const deliveryCopyOptions = {
      showCount: state.showCount,
      quantity: this.#deliveryZipCheck?.quantity ?? state.quantity,
    };
    const storeRow = this.#getStoreRowPresentation(
      state.status,
      state.pickUpEnabled,
      inventoryCopyOptions
    );
    const storeDisplayStatus = state.status === 'no_store' ? 'loading' : storeRow.displayStatus;

    const deliveryContext = this.#deliveryZipCheck
      ? { zip: this.#deliveryZipCheck.zip, inServiceArea: this.#deliveryZipCheck.inServiceArea }
      : {};

    const translations = Theme.translations ?? {};
    const zipSublineTemplate =
      translations.how_to_get_it_delivery_zip_subline ?? 'ZIP: {{ zip }}';
    const addZipLabel =
      translations.how_to_get_it_delivery_enter_zip ?? 'Enter ZIP to check delivery';

    if (deliveryZipLine instanceof HTMLElement) {
      const zip = this.#deliveryZipCheck?.zip ?? '';
      if (zip) {
        deliveryZipLine.textContent = this.#interpolateZip(zipSublineTemplate, zip);
        deliveryZipLine.hidden = false;
      } else {
        deliveryZipLine.textContent = '';
        deliveryZipLine.hidden = true;
      }
    }

    if (deliveryStatusText instanceof HTMLElement) {
      if (!hasDeliveryZip) {
        if (deliveryRowStatus === 'loading') {
          deliveryStatusText.textContent =
            translations.store_inventory_loading ?? 'Checking availability…';
          deliveryStatusText.classList.remove('how-to-get-it__status-text--prompt');
        } else {
          deliveryStatusText.textContent = addZipLabel;
          deliveryStatusText.classList.add('how-to-get-it__status-text--prompt');
        }
      } else {
        deliveryStatusText.textContent = this.#getDeliveryCopy(
          deliveryRowStatus,
          deliveryCopyOptions,
          deliveryContext
        );
        deliveryStatusText.classList.remove('how-to-get-it__status-text--prompt');
      }
    }

    if (deliveryStatusDot instanceof HTMLElement) {
      deliveryStatusDot.hidden = !hasDeliveryZip;
    }

    if (storeStatusText instanceof HTMLElement) {
      storeStatusText.textContent = storeRow.text;
    }

    this.#applyStatusDot(deliveryStatusDot, deliveryStatus, deliveryDisplayStatus);
    this.#applyStatusDot(storeStatusDot, storeStatus, storeDisplayStatus);
  }

  /**
   * Single entry for store PDP presentation: ATC/sticky buttons.
   *
   * @param {StorePresentationSyncOptions} options
   */
  syncPresentation(options) {
    if (this.dataset.storeInventoryEnabled !== 'true') return;

    if (options.canAdd !== undefined) {
      this.#applyAddToCartOverride(options.canAdd);
    }
  }

  /**
   * @param {boolean} canAdd
   */
  syncAddToCartPresentation(canAdd) {
    if (this.dataset.storeInventoryEnabled !== 'true') return;
    this.#applyAddToCartOverride(canAdd);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} canAdd
   */
  #syncAddToCartButtonPresentation(button, canAdd) {
    const translations = Theme.translations ?? {};
    const addToCartLabel = translations.product_add_to_cart ?? 'Add to cart';
    const soldOutLabel = translations.product_sold_out ?? 'Sold out';

    button.disabled = !canAdd;

    const labelEl =
      button.querySelector('.add-to-cart-text__content > span:first-child') ??
      button.querySelector('.add-to-cart-text__content span span');
    if (labelEl) {
      labelEl.textContent = canAdd ? addToCartLabel : soldOutLabel;
    }
  }

  /**
   * @param {boolean} canAdd
   */
  #applyAddToCartOverride(canAdd) {
    const productId = this.dataset.productId?.trim();
    if (!productId) return;

    const productForm = getProductFormComponent(productId);

    if (!productForm) return;

    const addToCartContainers = productForm.querySelectorAll('add-to-cart-component');
    const acceleratedCheckout = productForm.querySelector('[ref="acceleratedCheckoutButtonContainer"]');

    addToCartContainers.forEach((container) => {
      const button = container.querySelector('[ref="addToCartButton"]');
      if (button instanceof HTMLButtonElement) {
        this.#syncAddToCartButtonPresentation(button, canAdd);
      }
    });

    if (acceleratedCheckout instanceof HTMLElement) {
      if (canAdd) {
        acceleratedCheckout.removeAttribute('hidden');
      } else {
        acceleratedCheckout.setAttribute('hidden', 'true');
      }
    }

    const sticky = document.querySelector(
      `sticky-add-to-cart[data-product-id="${productId}"]`
    );
    if (sticky instanceof HTMLElement) {
      const stickyButton = sticky.querySelector('[ref="addToCartButton"]');
      if (stickyButton instanceof HTMLButtonElement) {
        this.#syncAddToCartButtonPresentation(stickyButton, canAdd);
        sticky.dataset.variantAvailable = canAdd ? 'true' : 'false';
      }
    }

    this.dispatchEvent(
      new CustomEvent(STORE_ATC_PRESENTATION_CHANGED_EVENT, {
        bubbles: true,
        detail: {
          productId,
          canAdd,
        },
      })
    );
  }
}

if (!customElements.get('store-product-availability')) {
  customElements.define('store-product-availability', StoreProductAvailability);
}
